<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0">
        <!-- Two siblings: content + footer -->

        <!-- Content -->
        <v-container
            class="flex-grow-1 d-flex flex-column"
            style="min-height: 0; height: 100%"
        >
            <DataFetchLoadingComponent :isLoading="isLoadingFirestore" />

            <!-- Tabs -->
            <v-tabs v-model="tab" color="primary" class="flex-shrink-0">
                <v-tab value="teamleaders" width="35%">TeamLeaders</v-tab>
                <v-tab value="users" width="30%">Users</v-tab>
                <v-tab value="projects" width="35%">
                    <div class="d-flex align-center">
                        <span
                            >Projects
                            <span class="text-primary">{{
                                periodSuffixLabel
                            }}</span></span
                        >
                        <!-- inject our chooser -->
                        <ProjectsPeriodFilterMenu
                            class="ml-2"
                            :projectNames="doProjectMap"
                            :priorityMap="doPriorityMap"
                            :disabled="tab !== 'projects'"
                            @period-changed="handlePeriodChange"
                            ref="periodFilterMenu"
                        />
                    </div>
                </v-tab>
            </v-tabs>

            <!-- Tab Content -->
            <v-tabs-window
                v-model="tab"
                class="flex-grow-1 d-flex flex-column"
                style="min-height: 0; height: 100%; overflow: visible"
            >
                <!-- Team Leaders Tab Content -->
                <v-tabs-window-item
                    v-if="tab === 'teamleaders'"
                    value="teamleaders"
                    class="d-flex flex-column flex-grow-1"
                    style="min-height: 0"
                >
                    <v-row style="min-height: 0">
                        <v-col style="height: 100%">
                            <TeamLeadersTable />
                        </v-col>
                    </v-row>
                </v-tabs-window-item>

                <!-- Users Tab Content -->
                <v-tabs-window-item
                    v-if="tab === 'users'"
                    value="users"
                    class="d-flex flex-column flex-grow-1"
                    style="min-height: 0"
                >
                    <v-row style="min-height: 0">
                        <v-col style="height: 100%">
                            <UsersTable />
                        </v-col>
                    </v-row>
                </v-tabs-window-item>

                <!-- Projects Tab Content -->
                <v-tabs-window-item
                    v-if="tab === 'projects'"
                    value="projects"
                    class="d-flex flex-column flex-grow-1"
                    style="min-height: 0"
                >
                    <v-row style="min-height: 0">
                        <v-col style="height: 100%">
                            <ProjectsTable
                                :tableData="projectsReportData"
                                :selected-period="selectedPeriod"
                                :enableReordering="false"
                                :enablePriority="true"
                                :enableClickNavigation="true"
                                :navigationRoute="'ProjectInfo'"
                                :navigationParam="'projectId'"
                                :currentTab="'projects'"
                            />
                        </v-col>
                    </v-row>
                </v-tabs-window-item>
            </v-tabs-window>

            <!-- Snackbar for Notifications -->
            <v-row class="flex-shrink-0">
                <v-col>
                    <MessageSnackbar
                        :message="snackbar.message"
                        :color="snackbar.color"
                        :timeout="snackbar.timeout"
                        :show="snackbar.show"
                        @update:show="snackbar.show = $event"
                    />
                </v-col>
            </v-row>
        </v-container>

        <!-- Footer - users tab -->
        <BottomButtonWrapper :isShowing="tab === 'users' && canAddNewUser">
            <v-col class="d-flex justify-center" cols="6">
                <AddDialog
                    :title="'Add New User'"
                    :button-text="'ADD NEW USER'"
                    :type="'user'"
                    :fields="[
                        {
                            key: 'inviteEmail',
                            label: 'User Email',
                            type: 'text',
                            rules: [rules.required, rules.email],
                        },
                        {
                            key: 'role',
                            label: 'Role',
                            type: 'select',
                            options: roles,
                            rules: [rules.required],
                        },
                    ]"
                />
            </v-col>
        </BottomButtonWrapper>

        <!-- Footer - projects tab -->
        <BottomButtonWrapper
            :isShowing="
                tab === 'projects' && (canResetPriorities || canAddNewProject)
            "
        >
            <v-col
                class="d-flex justify-center"
                cols="6"
                v-show="canResetPriorities"
            >
                <v-menu
                    :open-on-hover="!$vuetify.display.smAndDown"
                    location="top"
                >
                    <template v-slot:activator="{ props }">
                        <ThemedActionButton
                            :buttonColor="'error'"
                            :text="' RESET PRIORITY'"
                            v-bind="props"
                        >
                        </ThemedActionButton>
                    </template>

                    <v-list>
                        <v-list-item>
                            <v-btn
                                @click="confirmResetProjectsPriority"
                                variant="flat"
                                class="custom-text-color-error"
                                >PROJECTS PRIORITY
                            </v-btn>
                        </v-list-item>
                        <v-list-item>
                            <v-btn
                                @click="resetProjectPrioritiesForAllUsers"
                                variant="flat"
                                class="custom-text-color-error"
                            >
                                USERS PRIORITY</v-btn
                            ></v-list-item
                        >
                    </v-list>
                </v-menu>
            </v-col>
            <v-col cols="6" v-if="canAddNewProject">
                <AddDialog
                    title="Add New Project"
                    button-text="ADD NEW PROJECT"
                    type="project"
                    :fields="[
                        {
                            key: 'name',
                            label: 'Project Name *',
                            type: 'text',
                            rules: [rules.required],
                        },
                        {
                            key: 'description',
                            label: 'Description *',
                            type: 'text',
                            rules: [rules.required],
                        },
                        {
                            key: 'client',
                            label: 'Client',
                            type: 'text',
                            options: clientOptions,
                        },
                        {
                            key: 'technicalSupport',
                            label: 'Technical Support',
                            type: 'text',
                        },
                        {
                            key: 'teamLeaders',
                            label: 'Team Leaders *',
                            type: 'autocomplete-multiple',
                            options: teamLeaderOptions,
                            required: true,
                            max: 2,
                        },
                        {
                            key: 'isActive',
                            label: 'Status',
                            type: 'select',
                            options: [
                                { title: 'Active', value: true },
                                { title: 'Inactive', value: false },
                            ],
                        },
                    ]"
                />
            </v-col>
        </BottomButtonWrapper>
    </div>
</template>

<script>
import TeamLeadersTable from "./TeamLeadersTable.vue";
import UsersTable from "./UsersTable.vue";
import ProjectsTable from "./ProjectsTable.vue";
import { VTabs, VTab } from "vuetify/components";
import { mapGetters } from "vuex";
import ProjectsPeriodFilterMenu from "@/components/ProjectsPeriodFilterMenu.vue";
import DataFetchLoadingComponent from "@/components/DataFetchLoadingComponent.vue";
import BottomButtonWrapper from "@/components/BottomButtonWrapper.vue";
import AddDialog from "@/components/AddDialog.vue";
import ThemedActionButton from "@/components/ThemedActionButton.vue";
import MessageSnackbar from "@/components/MessageSnackbar.vue";
import { adminAndTeamLeadDashboardMixin } from "@/mixins/adminAndTeamLeadDashboardMixin";

export default {
    /**
     * Components used in this component.
     */
    components: {
        TeamLeadersTable,
        UsersTable,
        ProjectsTable,
        VTab,
        VTabs,
        ProjectsPeriodFilterMenu,
        DataFetchLoadingComponent,
        BottomButtonWrapper,
        AddDialog,
        ThemedActionButton,
        MessageSnackbar,
    },
    /**
     * Mixins used in this component.
     */
    mixins: [adminAndTeamLeadDashboardMixin],
    /**
     * Component's reactive data properties.
     * @returns {Object} An object containing the tab identifier.
     */
    data() {
        /**
         * Currently active tab. Defaults to 'users'.
         * @type {string}
         */
        return {
            tab: "users",
            /**
             * Available roles for a user.
             * @type {Array<String>}
             */
            roles: ["operator", "admin", "teamLead"],
            /**
             * Validation rules for form fields.
             * @type {Object}
             */
            rules: {
                required: (value) => !!value || "Required.",
                email: (value) =>
                    /.+@.+\..+/.test(value) || "E-mail must be valid.",
            },
            // snackbar comes from mixin
        };
    },
    computed: {
        ...mapGetters({
            user: "getUser",
            projects: "getManagedProjects",
            users: "getUsers",
            getRights: "getRights",
            getCompanies: "getCompanies",
        }),
        // periodSuffixLabel, doProjectMap, doPriorityMap come from mixin
        // canAddNewProject, canResetPriorities, teamLeaderOptions, clientOptions come from mixin

        /**
         * Determines if the current user can add new users.
         * @returns {Boolean} True if the user has permission to add new users.
         */
        canAddNewUser() {
            return this.getRights?.addNewUser || false;
        },
    },
    methods: {
        // confirmResetProjectsPriority, resetProjectPrioritiesForAllUsers come from mixin
    },
    // watch comes from mixin
    // mounted comes from mixin
};
</script>
